import React, { useState, useEffect } from "react"
import { lumi } from "../lib/lumi"
import toast from "react-hot-toast"
import {Mail, TrendingUp, BarChart3, Activity, CheckCircle, XCircle, Clock} from 'lucide-react'
import SuperadminLayout from "../components/SuperadminLayout"

export default function SuperadminEmailAnalytics() {
  const [emailMetrics, setEmailMetrics] = useState({
    delivered_30d: 0,
    opened_30d: 0,
    clicked_30d: 0,
    accepted_30d: 0
  })

  const [inviteMetrics, setInviteMetrics] = useState({
    active: 0,
    used_30d: 0,
    expired_30d: 0,
    acceptance_pct: "0%"
  })

  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchMetrics()
  }, [])

  const fetchMetrics = async () => {
    try {
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)

      // Fetch invite metrics
      const [activeInvites, usedInvites, expiredInvites] = await Promise.all([
        lumi.entities.Invites.list({
          filter: { status: "pending" }
        }),
        lumi.entities.Invites.list({
          filter: {
            status: "used",
            createdAt: { $gte: thirtyDaysAgo.toISOString() }
          }
        }),
        lumi.entities.Invites.list({
          filter: {
            status: "expired",
            expires_at: { $gte: thirtyDaysAgo.toISOString() }
          }
        })
      ])

      const total = usedInvites.total + expiredInvites.total
      const acceptance = total > 0 ? ((usedInvites.total / total) * 100).toFixed(1) : "0"

      setInviteMetrics({
        active: activeInvites.total,
        used_30d: usedInvites.total,
        expired_30d: expiredInvites.total,
        acceptance_pct: `${acceptance}%`
      })

      // Email metrics (placeholder - requires email provider webhooks)
      setEmailMetrics({
        delivered_30d: 245,
        opened_30d: 198,
        clicked_30d: 156,
        accepted_30d: 89
      })
    } catch (error) {
      console.error("Error fetching analytics metrics:", error)
      toast.error("Failed to load analytics")
    } finally {
      setLoading(false)
    }
  }

  return (
    <SuperadminLayout activeMenuItem="email_analytics">
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex items-center gap-3 mb-6">
          <Mail className="w-8 h-8 text-amber-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Email & Invite Analytics</h1>
            <p className="text-sm text-gray-600">Track email delivery, invites, and acceptance rates</p>
          </div>
        </div>
        {/* Email Delivery Note */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-700">
            <strong>Note:</strong> Email analytics require webhook integration with your email provider (e.g., SendGrid, Mailgun).
            Current email metrics are placeholders for demonstration. Invite metrics are live from the database.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <Activity className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading analytics...</p>
          </div>
        ) : (
          <>
            {/* Section 1: Email Metrics */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Email Delivery Metrics (Last 30 Days)</h2>
              <div className="grid md:grid-cols-4 gap-4 mb-6">
                <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border-2 border-blue-200">
                  <p className="text-sm text-blue-600 mb-1 font-medium">Delivered</p>
                  <p className="text-3xl font-bold text-blue-900">{emailMetrics.delivered_30d}</p>
                </div>
                <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border-2 border-purple-200">
                  <p className="text-sm text-purple-600 mb-1 font-medium">Opened</p>
                  <p className="text-3xl font-bold text-purple-900">{emailMetrics.opened_30d}</p>
                </div>
                <div className="p-4 bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg border-2 border-teal-200">
                  <p className="text-sm text-teal-600 mb-1 font-medium">Clicked</p>
                  <p className="text-3xl font-bold text-teal-900">{emailMetrics.clicked_30d}</p>
                </div>
                <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border-2 border-green-200">
                  <p className="text-sm text-green-600 mb-1 font-medium">Accepted</p>
                  <p className="text-3xl font-bold text-green-900">{emailMetrics.accepted_30d}</p>
                </div>
              </div>

              {/* Email Chart Placeholder */}
              <div className="p-6 bg-white border border-gray-200 rounded-lg text-center">
                <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600">Time-series chart coming soon</p>
                <p className="text-sm text-gray-500 mt-2">Opens & Acceptances (Last 90 Days)</p>
              </div>
            </div>

            {/* Section 2: Invite Metrics */}
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">Invite Metrics (Last 30 Days)</h2>
              <div className="grid md:grid-cols-4 gap-4 mb-6">
                <div className="p-4 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg border-2 border-yellow-200">
                  <p className="text-sm text-yellow-600 mb-1 font-medium">Active Invites</p>
                  <p className="text-3xl font-bold text-yellow-900">{inviteMetrics.active}</p>
                </div>
                <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border-2 border-green-200">
                  <p className="text-sm text-green-600 mb-1 font-medium">Used</p>
                  <p className="text-3xl font-bold text-green-900">{inviteMetrics.used_30d}</p>
                </div>
                <div className="p-4 bg-gradient-to-br from-red-50 to-red-100 rounded-lg border-2 border-red-200">
                  <p className="text-sm text-red-600 mb-1 font-medium">Expired</p>
                  <p className="text-3xl font-bold text-red-900">{inviteMetrics.expired_30d}</p>
                </div>
                <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border-2 border-purple-200">
                  <p className="text-sm text-purple-600 mb-1 font-medium">Acceptance Rate</p>
                  <p className="text-3xl font-bold text-purple-900">{inviteMetrics.acceptance_pct}</p>
                </div>
              </div>

              {/* Invite Chart Placeholder */}
              <div className="p-6 bg-white border border-gray-200 rounded-lg text-center">
                <TrendingUp className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600">Time-series chart coming soon</p>
                <p className="text-sm text-gray-500 mt-2">Invites Sent vs Used (Last 90 Days)</p>
              </div>
            </div>
          </>
        )}
      </div>
    </SuperadminLayout>
  )
}
